/**
 * The rules this account can see, and the pickers a rule is written from.
 *
 * Loading is lazy and once: the Rules room asks when it is first opened, and
 * the entity and series lists are fetched per source and per entity as the
 * editor needs them. Nothing here is fetched at boot - Argus is one team's
 * room, and everybody else must pay nothing for it.
 *
 * Every failure resolves to "we do not know" rather than to an exception with
 * a spinner behind it: `docs/plans/argus.md` rule 9 is that Argus says what it does not
 * know, and a list that silently stays empty is the opposite of that.
 */

import {
  getArgusFindings,
  listArgusWatchers,
  createArgusRule,
  deleteArgusRule,
  setArgusRuleStatus,
  setArgusRuleCharts,
  setArgusRuleSchedule,
  listArgusEntities,
  listArgusRules,
  listArgusSeries,
  listArgusSources,
  updateArgusRule,
  listArgusTeams,
  createArgusFolder,
  renameArgusFolder,
  deleteArgusFolder,
  setArgusRuleFolder,
  setArgusRuleVisibility,
  followArgusRule,
} from '../../api/endpoints.ts';
import type {
  ArgusEntity,
  ArgusFindings,
  ArgusRule,
  ArgusRuleDocument,
  ArgusRuleChart,
  ArgusSchedule,
  ArgusSeries,
  ArgusSource,
  ArgusTreeDocument,
  ArgusTeams,
} from '../../api/types.ts';
import { Atom } from '../../state/atom.ts';
import { argusClock } from './argus-lens.ts';
import { userMessage } from '../../api/errors.ts';

export const rules = new Atom<ArgusRule[]>([]);
export const sources = new Atom<ArgusSource[]>([]);
export const entities = new Atom<ArgusEntity[]>([]);
export const seriesForEntity = new Atom<ArgusSeries[]>([]);

/** The teams on the Rules room's rail, with their folders (decision 35). */
export const teams = new Atom<ArgusTeams>({ teams: [], sees_all: false });
/** Whether `teams` came from the server, so "in no team" is not shown while it loads. */
export const teamsLoaded = new Atom(false);

/** Argus is open to this person, but every rule, alert and folder belongs to a team and they are in none. */
export function inNoTeam(): boolean {
  const t = teams.value;
  return teamsLoaded.value && !t.sees_all && !t.teams.some((team) => team.in_team);
}

/**
 * Where a rule written now goes: the team and folder open on the rail when
 * "Write a rule" was pressed. Read by `saveRule` for a new rule only; with
 * nothing set, the server puts it in the author's only team.
 */
export const newRulePlace = new Atom<{ teamId: string; folderId: string | null } | null>(null);

/**
 * An unsaved change the Assistant proposed to a *deployed* rule, on its way to
 * the block editor. Set by the Assistant's card, taken (and cleared) by the
 * Rules room. Never saved from here: saving a deployed rule changes what runs,
 * so the person saves it in the editor. (Anything else the Assistant writes
 * is already stored as a draft, and its card opens that rule.)
 */
export interface AssistantRuleDraft {
  /** The rule this changes, or null for a new one. */
  ruleId: string | null;
  name: string;
  description?: string;
  document: ArgusTreeDocument;
  assumptions: string[];
}
export const assistantDraft = new Atom<AssistantRuleDraft | null>(null);

/**
 * The block editor that is open now, as the Assistant is told about it: which
 * rule (or a Backtest question), and what is in it, unsaved changes included.
 * Set by the editor while it is drawn, cleared when it closes.
 */
export interface OpenEditorState {
  mode: 'rule' | 'question';
  ruleId: string | null;
  version: number | null;
  status: string | null;
  name: string;
  description: string;
  document: ArgusTreeDocument;
  dirty: boolean;
  readonly: boolean;
}
export const openEditor = new Atom<OpenEditorState | null>(null);

/**
 * A change the Assistant made to the open editor, on its way into it. The
 * editor takes it only when it is still the editor it was written for (same
 * mode and rule), applies it unsaved with an Undo, and clears it.
 */
export interface AssistantEditorEdit {
  mode: 'rule' | 'question';
  ruleId: string | null;
  document: ArgusTreeDocument;
  name: string | null;
  description: string | null;
  summary: string;
}
export const assistantEditorEdit = new Atom<AssistantEditorEdit | null>(null);

/** `idle` before anything was asked; `failed` carries a sentence, never a code. */
export const rulesStatus = new Atom<'idle' | 'loading' | 'ready' | 'failed'>('idle');
export const rulesError = new Atom<string | null>(null);

function messageFrom(error: unknown): string {
  return userMessage(error, 'Argus could not be reached.');
}

/** The findings of the rule open in the drawer, by rule id. */
export const findings = new Atom<Record<string, ArgusFindings | 'loading' | 'failed'>>({});

export async function loadFindings(ruleId: string): Promise<void> {
  findings.set({ ...findings.value, [ruleId]: findings.value[ruleId] ?? 'loading' });
  try {
    const answer = await getArgusFindings(ruleId);
    findings.set({ ...findings.value, [ruleId]: answer });
  } catch {
    findings.set({ ...findings.value, [ruleId]: 'failed' });
  }
}

function minutesWords(seconds: number): string {
  const minutes = Math.round(seconds / 60);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.round(minutes / 60);
  return `${hours} hour${hours === 1 ? '' : 's'} ago`;
}

/** Ask who is watching, and put the answer in the header. */
export async function loadWatchers(): Promise<void> {
  try {
    const answer = await listArgusWatchers();
    const live = answer.watchers.filter((w) => !w.quiet);
    const newest = answer.watchers[0];
    argusClock.set(
      live.length
        ? `Daily clock · watcher seen ${minutesWords(live[0]?.silent_seconds ?? 0)}`
        : newest
          ? `Daily clock · watcher quiet since ${minutesWords(newest.silent_seconds)}`
          : 'Daily clock · no watcher connected',
    );
  } catch {
    argusClock.set('Daily clock · watcher status unknown');
  }
}

export async function loadTeams(): Promise<void> {
  try {
    const next = await listArgusTeams();
    teamsLoaded.set(true);
    teams.set(next);
  } catch (error) {
    rulesError.set(messageFrom(error));
  }
}

/**
 * The rail's actions. Each answers null on success or the database's
 * sentence - the team, the leader, the folder that was missing - and reloads
 * what it changed.
 */
async function act(run: () => Promise<unknown>, reload: { rules?: boolean; teams?: boolean }): Promise<string | null> {
  try {
    await run();
    await Promise.all([reload.rules ? loadRules() : null, reload.teams ? loadTeams() : null]);
    return null;
  } catch (error) {
    return messageFrom(error);
  }
}

export const createFolder = (teamId: string, name: string): Promise<string | null> =>
  act(() => createArgusFolder(teamId, name), { teams: true });

export const renameFolder = (id: string, name: string): Promise<string | null> =>
  act(() => renameArgusFolder(id, name), { teams: true, rules: true });

export const deleteFolder = (id: string): Promise<string | null> =>
  act(() => deleteArgusFolder(id), { teams: true, rules: true });

export const moveRuleToFolder = (ruleId: string, folderId: string | null): Promise<string | null> =>
  act(() => setArgusRuleFolder(ruleId, folderId), { rules: true });

export const setVisibility = (ruleId: string, visibility: 'team' | 'public'): Promise<string | null> =>
  act(() => setArgusRuleVisibility(ruleId, visibility), { rules: true });

export async function setFollow(ruleId: string, follow: boolean): Promise<string | null> {
  const failed = await act(() => followArgusRule(ruleId, follow), { rules: true });
  if (!failed) {
    // A followed rule's alerts now reach the Inbox and the dimmed count.
    const inbox = await import('./argus-inbox-store.ts');
    void inbox.loadAttention();
  }
  return failed;
}

export async function loadRules(): Promise<void> {
  rulesStatus.set(rulesStatus.value === 'ready' ? 'ready' : 'loading');
  rulesError.set(null);
  try {
    const answer = await listArgusRules();
    rules.set(answer.rules ?? []);
    rulesStatus.set('ready');
  } catch (error) {
    rulesError.set(messageFrom(error));
    rulesStatus.set('failed');
  }
}

export async function loadSources(): Promise<void> {
  if (sources.value.length) return;
  try {
    const answer = await listArgusSources();
    sources.set(answer.sources ?? []);
  } catch (error) {
    rulesError.set(messageFrom(error));
  }
}

export async function loadEntities(sourceKey: string): Promise<void> {
  try {
    const answer = await listArgusEntities(sourceKey);
    entities.set(answer.entities ?? []);
  } catch (error) {
    entities.set([]);
    rulesError.set(messageFrom(error));
  }
}

export async function loadSeries(sourceKey: string, entityId: string): Promise<void> {
  seriesForEntity.set([]);
  if (!entityId) return;
  try {
    const answer = await listArgusSeries(sourceKey, entityId);
    seriesForEntity.set(answer.series ?? []);
  } catch (error) {
    rulesError.set(messageFrom(error));
  }
}

/**
 * Write a rule, or a new version of one.
 *
 * Returns the error sentence rather than throwing it: the form shows what to
 * fix beside the field, and a rejected rule is an ordinary outcome of writing
 * one, not an exception.
 */
export async function saveRule(
  id: string | null,
  name: string,
  document: ArgusRuleDocument | ArgusTreeDocument,
  description?: string,
): Promise<{ ok: true; rule: ArgusRule } | { ok: false; error: string }> {
  try {
    const place = newRulePlace.value;
    const rule = id
      ? await updateArgusRule(id, { name, document, description })
      : await createArgusRule({
          name,
          document,
          description,
          group_id: place?.teamId,
          folder_id: place?.folderId ?? undefined,
        });
    await loadRules();
    // Your own draft is drawn on your own charts, so a new one changes what
    // they show - see `argus-overlays.ts`.
    const overlays = await import('./argus-overlays.ts');
    overlays.invalidateArgusMarks();
    return { ok: true, rule };
  } catch (error) {
    return { ok: false, error: messageFrom(error) };
  }
}

/**
 * Deploy a rule, or retire it.
 *
 * The answer comes back as a sentence rather than an exception, because a
 * refusal here is ordinary: the database decides, and what it says - the
 * permission, or leadership of the rule's team - is what the person needs to
 * read. The marks a chart draws change with it, so they are re-read.
 */
export async function setStatus(
  id: string,
  status: 'draft' | 'deployed' | 'retired',
): Promise<string | null> {
  try {
    await setArgusRuleStatus(id, status);
    await loadRules();
    const overlays = await import('./argus-overlays.ts');
    overlays.invalidateArgusMarks();
    return null;
  } catch (error) {
    return messageFrom(error);
  }
}

/** Change when a rule runs. Null on success, or what the database said. */
export async function setSchedule(id: string, schedule: ArgusSchedule): Promise<string | null> {
  try {
    await setArgusRuleSchedule(id, schedule);
    await loadRules();
    return null;
  } catch (error) {
    return messageFrom(error);
  }
}

/** A rule's charts, saved at once: they are not part of the version. Null, or why not. */
export async function setCharts(id: string, charts: ArgusRuleChart[]): Promise<string | null> {
  try {
    const saved = await setArgusRuleCharts(id, charts);
    rules.set(rules.value.map((r) => (r.id === saved.id ? saved : r)));
    return null;
  } catch (error) {
    return messageFrom(error);
  }
}

export async function removeRule(id: string): Promise<string | null> {
  try {
    await deleteArgusRule(id);
    await loadRules();
    return null;
  } catch (error) {
    return messageFrom(error);
  }
}
